import { Component, Inject, inject, InjectionToken, NgModule } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AccountsAuxiliaryDataTableService,
  AccountsCustomFieldsData,
  AuxDataAccountsActionDependencies,
  AuxiliaryDataAccountsTableComponent,
} from '@vendasta/auxiliary-data-components';
import {
  AccountListsDialogData,
  AccountListsModalComponent,
  ActionLists,
  ActionListsDependencies,
} from '@vendasta/businesses';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { DataSourceSettings, VaFilteredMatTableService } from '@vendasta/va-filter2-table';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce, shareReplay, switchMap, take } from 'rxjs/operators';
import { ActionListsApiService } from '../action-lists/action-lists.api.service';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { MarketsService } from '../core/markets/markets.service';

@Component({
  selector: 'app-filterby-custom-fields-page',
  template: `
    <glxy-page>
      <glxy-page-toolbar>
        <glxy-page-title data-testid="filter-by-custom-fields-page-title">
          {{ 'MANAGE_ACCOUNTS.AUXILIARY_DATA_FILTER.PAGE_TITLE' | translate }}
        </glxy-page-title>
      </glxy-page-toolbar>
      <ng-template *ngTemplateOutlet="auxDataAccountsTable"></ng-template>
    </glxy-page>
    
    <ng-template #auxDataAccountsTable>
      @if (hasAccessToList$ | async; as hasAccessToList) {
        <aux-auxiliary-data-accounts-table
          [enableAddToListButton]="hasAccessToList"
        ></aux-auxiliary-data-accounts-table>
      }
    </ng-template>
    `,
  styles: [':host { display: block; }', '.info { margin-left: 8px; }'],
  standalone: false,
})
export class FilterbyCustomFieldsPageComponent {
  hasAccessToList$ = this.accessService.hasAccessToFeature(Feature.lists);

  constructor(@Inject(StubAccessService) private accessService: AccessService) {}
}

export const ActionListsLibraryInjectionToken = new InjectionToken<ActionListsDependencies>(
  'dependencies given to action-list in businesses library',
  {
    factory: () => {
      const actionListsService: ActionListsApiService = inject(ActionListsApiService);
      const marketsService: MarketsService = inject(MarketsService);
      return {
        totalPageSize: 200,
        listActionLists(
          partnerId: string,
          marketId: string,
          searchString?: string,
          pageIndex?: number,
          pageSize?: number,
        ): Observable<ActionLists> {
          const accessibleMarkets$ = marketsService.markets.pipe(
            map((markets) => markets.map((market) => market.market_id)),
            shareReplay({ refCount: true, bufferSize: 1 }),
          );
          const nextActionList = (resp) => {
            if (resp.hasMore) {
              return accessibleMarkets$.pipe(
                take(1),
                switchMap((accessibleMarkets) =>
                  actionListsService.lookupActionLists(resp.nextPage, accessibleMarkets, pageSize),
                ),
              );
            } else {
              return EMPTY;
            }
          };
          return accessibleMarkets$.pipe(
            take(1),
            switchMap((accessibleMarkets) =>
              actionListsService.lookupActionLists(pageIndex, accessibleMarkets, pageSize),
            ),
            expand(nextActionList),
            reduce(
              (acc, curr) => {
                return {
                  actionLists: acc.actionLists.concat(curr?.actionLists || []),
                  nextPage: curr.nextPage,
                  totalActionListsCount: curr.totalActionListsCount,
                };
              },
              { actionLists: [], totalActionListsCount: 0, nextPage: 0 } as ActionLists,
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
          );
        },
        addAccountsToActionList(
          partnerId: string,
          marketId: string,
          listName: string,
          accountGroupIds: string[],
        ): Observable<string> {
          return actionListsService.addAccountsToActionList(listName, accountGroupIds);
        },
      };
    },
  },
);

export const AuxDataAccountsActionInjectionToken = new InjectionToken<AuxDataAccountsActionDependencies>(
  'dependencies for the auxiliary-data account table actions',
  {
    factory: () => {
      const dialog: MatDialog = inject(MatDialog);

      return {
        openAccountListsModal(
          event: DataSourceSettings,
          vaFilteredMatTableService: VaFilteredMatTableService<AccountsCustomFieldsData>,
          accountsAuxiliaryDataTableService: AccountsAuxiliaryDataTableService,
        ): void {
          dialog.closeAll();
          const dialogData: AccountListsDialogData = {
            vaFilteredMatTableService: vaFilteredMatTableService,
            tableDataService: accountsAuxiliaryDataTableService,
            currentTableDataSettings: event,
          };
          dialog.open(AccountListsModalComponent, { data: dialogData });
        },
      };
    },
  },
);

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule,
    GalaxyPageModule,
    AuxiliaryDataAccountsTableComponent,
  ],
  declarations: [FilterbyCustomFieldsPageComponent],
  exports: [FilterbyCustomFieldsPageComponent],
})
export class FilterbyCustomFieldsPageComponentModule {}
